import type { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { now } from '@seen/core/utils'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { z } from 'zod'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { useForm } from '#src/hooks/useForm.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { trimHTML } from '#src/utils/trimHTML.ts'

const formSchema = z.object({
  message: z
    .string()
    .transform(trimHTML)
    .refine((v) => v.length > 0, { message: 'Message required' }),
})

export function CommentEditor({
  comment,
  onClose,
}: {
  comment: CommentCommunication
  onClose: () => void
}): JSX.Element {
  const form = useForm({
    schema: formSchema,
    defaultValues: {
      message: comment.message,
    },
  })

  const mutateComment = usePatchMutation<CommentCommunication>().mutate

  const handleSubmit = form.handleSubmit((values) => {
    if (!values.message.trim()) {
      return
    }

    mutateComment({
      instance: comment,
      mutateFn: (c) => {
        c.message = values.message
        c.editedDate = now()
      },
    })

    onClose()
  })

  return (
    <Form onSubmit={handleSubmit} className="flex flex-col gap-1.5">
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => (
          <RichTextEditor
            placeholder="Leave a comment…"
            variant="unstyled"
            related={[comment.reference]}
            {...field}
          />
        )}
      />
      <div className="flex justify-end gap-2">
        <Button size="icon" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="icon" variant="outline">
          Save
        </Button>
      </div>
    </Form>
  )
}
